import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PropertyCard from '@/components/PropertyCard';
import Seo from '@/components/Seo';
import { getProperties, getCitySummary, toggleSaveProperty, getSavedProperties } from '@/lib/api';
import { CITY_META, LOCALITIES, DEFAULT_CITY_IMAGE, slugify } from '@/lib/cities';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { MapPin, Building2, Home, KeyRound, TrendingUp, ArrowRight } from 'lucide-react';

const PAGE_SIZE = 24;

const formatPrice = (price) => {
  if (!price) return '—';
  if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
  if (price >= 100000) return `₹${(price / 100000).toFixed(1)} Lac`;
  return `₹${price.toLocaleString('en-IN')}`;
};

const CityLandingPage = () => {
  const { citySlug } = useParams();
  const { user } = useAuth();
  const slug = (citySlug || '').toLowerCase();
  const meta = CITY_META[slug] || {
    name: slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : 'India',
    state: 'India',
    image: DEFAULT_CITY_IMAGE,
    blurb: 'verified homes, apartments and plots',
  };
  const city = meta.name;
  const localities = LOCALITIES[slug] || [];

  const [summary, setSummary] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());

  useEffect(() => {
    window.scrollTo(0, 0);
    const load = async () => {
      setLoading(true);
      try {
        const [sum, props] = await Promise.all([
          getCitySummary(city),
          getProperties({ city, skip: 0, limit: PAGE_SIZE }),
        ]);
        setSummary(sum);
        setProperties(props);
        setHasMore(props.length === PAGE_SIZE);
        if (user) {
          const saved = await getSavedProperties();
          setSavedIds(new Set(saved.map((p) => p.property_id)));
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, user]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const next = await getProperties({ city, skip: properties.length, limit: PAGE_SIZE });
      setProperties((prev) => [...prev, ...next]);
      setHasMore(next.length === PAGE_SIZE);
    } catch (e) {
      toast.error('Failed to load more');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSave = async (propertyId) => {
    if (!user) { toast.error('Please sign in to save properties'); return; }
    try {
      const res = await toggleSaveProperty(propertyId);
      setSavedIds((prev) => {
        const s = new Set(prev);
        if (res.saved) s.add(propertyId); else s.delete(propertyId);
        return s;
      });
    } catch (e) { toast.error('Failed to save'); }
  };

  // JSON-LD structured data for this city collection
  useEffect(() => {
    const id = 'city-jsonld';
    document.getElementById(id)?.remove();
    const priceRange = summary && summary.min_price && summary.max_price
      ? `${formatPrice(summary.min_price)} - ${formatPrice(summary.max_price)}` : undefined;
    const data = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `Property in ${city}`,
      description: `Buy, sell and rent verified property in ${city}, ${meta.state}.`,
      url: `https://bharat.estate/real-estate/${slug}`,
      about: { '@type': 'Place', name: city, address: { '@type': 'PostalAddress', addressLocality: city, addressRegion: meta.state, addressCountry: 'IN' } },
      ...(priceRange ? { offers: { '@type': 'AggregateOffer', priceCurrency: 'INR', offerCount: summary?.total || 0 } } : {}),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
    return () => { document.getElementById(id)?.remove(); };
  }, [summary, city, slug, meta.state]);

  const total = summary?.total ?? properties.length;

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="city-landing-page">
      <Seo
        title={`Property in ${city} — Buy, Sell & Rent Homes`}
        description={`Explore ${total}+ verified properties for sale and rent in ${city}, ${meta.state}. Apartments, villas, plots & commercial spaces across ${meta.blurb}. 0% brokerage on Bharat Estate.`}
        path={`/real-estate/${slug}`}
        image={meta.image}
      />

      {/* Hero */}
      <section className="relative" data-testid="city-hero">
        <div className="absolute inset-0">
          <img src={meta.image} alt={`Real estate in ${city}`} className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.src = DEFAULT_CITY_IMAGE; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/70 to-[#0F172A]/40" />
        </div>
        <div className="relative container-luxury py-20 md:py-28 pt-28 md:pt-36">
          <nav className="text-white/70 text-sm mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D4AF37]">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/properties" className="hover:text-[#D4AF37]">Properties</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{city}</span>
          </nav>
          <p className="caption text-[#D4AF37] mb-3 flex items-center gap-2">
            <MapPin className="h-4 w-4" /> {meta.state}, India
          </p>
          <h1 className="font-['Plus_Jakarta_Sans'] text-white text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight max-w-3xl">
            Property for Sale & Rent in {city}
          </h1>
          <p className="text-white/80 text-base md:text-lg mt-4 max-w-2xl">
            Discover {total}+ verified homes across {meta.blurb} in {city}. Apartments, villas, plots
            and commercial spaces — all with 0% brokerage.
          </p>

          {/* Stat pills */}
          <div className="flex flex-wrap gap-3 mt-8">
            <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-xl px-5 py-3" data-testid="city-stat-total">
              <p className="text-[#D4AF37] text-2xl font-semibold">{total}+</p>
              <p className="text-white/70 text-xs">Live listings</p>
            </div>
            {summary?.for_sale != null && (
              <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-xl px-5 py-3" data-testid="city-stat-sale">
                <p className="text-white text-2xl font-semibold">{summary.for_sale}</p>
                <p className="text-white/70 text-xs">For sale</p>
              </div>
            )}
            {summary?.for_rent != null && (
              <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-xl px-5 py-3" data-testid="city-stat-rent">
                <p className="text-white text-2xl font-semibold">{summary.for_rent}</p>
                <p className="text-white/70 text-xs">For rent</p>
              </div>
            )}
            {summary?.min_price && (
              <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-xl px-5 py-3" data-testid="city-stat-price">
                <p className="text-white text-2xl font-semibold flex items-center gap-1">
                  <TrendingUp className="h-4 w-4 text-[#D4AF37]" /> {formatPrice(summary.min_price)}+
                </p>
                <p className="text-white/70 text-xs">Starting price</p>
              </div>
            )}
          </div>

          {/* Quick CTAs */}
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to={`/properties?city=${encodeURIComponent(city)}&listing_type=buy`}>
              <Button className="bg-[#D4AF37] hover:bg-[#c19f30] text-[#0F172A] font-medium" data-testid="city-cta-buy">
                <Home className="h-4 w-4 mr-2" /> Buy in {city}
              </Button>
            </Link>
            <Link to={`/properties?city=${encodeURIComponent(city)}&listing_type=rent`}>
              <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="city-cta-rent">
                <KeyRound className="h-4 w-4 mr-2" /> Rent in {city}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Listings */}
      <section className="container-luxury py-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="caption mb-2">Handpicked in {city}</p>
            <h2 className="heading-secondary text-[#0F172A]">Latest Properties in {city}</h2>
          </div>
          <Link to={`/properties?city=${encodeURIComponent(city)}`}
            className="hidden md:flex items-center gap-2 text-[#0F172A] hover:text-[#D4AF37] font-medium" data-testid="city-view-all">
            View all with filters <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-sm animate-pulse">
                <div className="aspect-[4/3] bg-gray-200" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : properties.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="city-listings-grid">
              {properties.map((p) => (
                <PropertyCard key={p.property_id} property={p} onSave={handleSave} isSaved={savedIds.has(p.property_id)} />
              ))}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-10">
                <Button onClick={loadMore} disabled={loadingMore} variant="outline"
                  className="px-10 py-6 text-base border-[#0F172A]/20 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  data-testid="city-load-more-btn">
                  {loadingMore ? 'Loading…' : `Load More in ${city}`}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20 bg-white rounded-sm">
            <Building2 className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-xl font-medium text-[#0F172A] mb-2">No listings in {city} yet</h3>
            <p className="text-muted-foreground mb-6">Explore properties across other cities.</p>
            <Link to="/properties"><Button variant="outline" data-testid="city-browse-all">Browse all properties</Button></Link>
          </div>
        )}
      </section>

      {/* Popular localities */}
      {localities.length > 0 && (
        <section className="bg-[#0F172A]" data-testid="city-localities">
          <div className="container-luxury py-14">
            <p className="caption text-[#D4AF37] mb-2">Explore by area</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold mb-8">Popular localities in {city}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {localities.map((loc) => (
                <Link
                  key={loc}
                  to={`/real-estate/${slug}/${slugify(loc)}`}
                  className="group flex items-center justify-between bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#D4AF37]/50 rounded-xl px-4 py-3 transition-colors"
                  data-testid={`locality-link-${slugify(loc)}`}
                >
                  <span className="text-white/90 text-sm font-medium">Property in {loc}</span>
                  <ArrowRight className="h-4 w-4 text-white/40 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* SEO copy */}
      <section className="bg-white border-t border-border/50">
        <div className="container-luxury py-14 prose max-w-3xl">
          <h2 className="font-['Plus_Jakarta_Sans'] text-3xl text-[#0F172A] font-semibold mb-4">
            Buying &amp; renting property in {city}
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            {city}, {meta.state} is one of India&apos;s most sought-after real estate markets, known for {meta.blurb}.
            On Bharat Estate you can compare {total}+ verified listings in {city} — from budget apartments to premium
            villas and commercial spaces — with transparent details and 0% brokerage. Use our EMI, affordability and
            stamp-duty calculators to plan your purchase, and reach out to sellers directly through our secure enquiry form.
          </p>
          <div className="flex flex-wrap gap-3 mt-6 not-prose">
            <Link to={`/properties?city=${encodeURIComponent(city)}&property_type=apartment`} className="px-4 py-2 rounded-full bg-[#F9F9F7] border border-border text-sm hover:border-[#D4AF37]">Apartments in {city}</Link>
            <Link to={`/properties?city=${encodeURIComponent(city)}&property_type=villa`} className="px-4 py-2 rounded-full bg-[#F9F9F7] border border-border text-sm hover:border-[#D4AF37]">Villas in {city}</Link>
            <Link to={`/properties?city=${encodeURIComponent(city)}&property_type=plot`} className="px-4 py-2 rounded-full bg-[#F9F9F7] border border-border text-sm hover:border-[#D4AF37]">Plots in {city}</Link>
            <Link to={`/properties?city=${encodeURIComponent(city)}&listing_type=rent`} className="px-4 py-2 rounded-full bg-[#F9F9F7] border border-border text-sm hover:border-[#D4AF37]">Rentals in {city}</Link>
            <Link to="/emi-calculator" className="px-4 py-2 rounded-full bg-[#F9F9F7] border border-border text-sm hover:border-[#D4AF37]">EMI Calculator</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityLandingPage;
